import { create } from "zustand";
import {
  getAllUsers,
  getAllRoles,
  createUser,
  updateUser,
  deleteUser,
  getDashboardStats
} from "@/services/AdminService/UserManagementService";
import { registerStoreReset } from "@/store/storeResetUtils";

interface User {
  id: string;
  email: string;
  name: string;
  contact: string;
  role_name: string;
  created_at: string;
  profile_photo?: string;
}

interface Role {
  id: string;
  role_name: string;
  role_detail: string;
}

interface DashboardStats {
  totalHR: number;
  totalProgramManagers: number;
  totalUsers: number;
}

interface AdminStore {
  // State
  users: User[];
  roles: Role[];
  dashboardStats: DashboardStats | null;
  loading: boolean;
  error: string | null;
  success: string | null;

  // Actions
  fetchUsers: () => Promise<void>;
  fetchRoles: () => Promise<void>;
  fetchDashboardStats: () => Promise<void>;
  createNewUser: (userData: any) => Promise<boolean>;
  updateExistingUser: (userId: string, userData: any) => Promise<boolean>;
  deleteExistingUser: (userId: string) => Promise<boolean>;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
  resetMessages: () => void;
  resetStore: () => void;
}

const useAdminStore = create<AdminStore>((set, get) => ({
  // Initial state
  users: [],
  roles: [],
  dashboardStats: null,
  loading: false,
  error: null,
  success: null,

  // Fetch all users
  fetchUsers: async () => {
    try {
      set({ loading: true, error: null });

      const response = await getAllUsers();
      console.log("Fetch Users API Response:", response);

      if (response.success) {
        // Convert relative photo URLs to full URLs
        const usersWithFullPhotoUrls = response.data.map((user: User) => ({
          ...user,
          profile_photo: user.profile_photo
            ? (user.profile_photo.startsWith('http')
              ? user.profile_photo
              : `http://localhost:5000${user.profile_photo}`)
            : undefined
        }));

        set({ users: usersWithFullPhotoUrls, loading: false });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch users',
          loading: false
        });
      }
    } catch (error: any) {
      console.error("Fetch Users Error:", error);
      set({ error: error.message, loading: false });
    }
  },

  // Fetch all roles - filtering is now done in the UI based on user permissions
  fetchRoles: async () => {
    try {
      const response = await getAllRoles();
      console.log("Fetch Roles API Response:", response);

      if (response.success) {
        // Return all roles - the UI component will filter based on user's is_super_admin status
        set({ roles: response.data });
      } else {
        console.error('Failed to fetch roles:', response.error);
      }
    } catch (error: any) {
      console.error("Fetch Roles Error:", error);
    }
  },

  // Fetch dashboard statistics
  fetchDashboardStats: async () => {
    try {
      set({ loading: true, error: null });

      const response = await getDashboardStats();
      console.log("Fetch Dashboard Stats API Response:", response);

      if (response.success) {
        set({ dashboardStats: response.data, loading: false });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch dashboard stats',
          loading: false
        });
      }
    } catch (error: any) {
      console.error("Fetch Dashboard Stats Error:", error);
      set({ error: error.message, loading: false });
    }
  },

  // Create a new user
  createNewUser: async (userData: any) => {
    try {
      set({ loading: true, error: null, success: null });

      const response = await createUser(userData);
      console.log("Create User API Response:", response);

      if (response.success) {
        set({ success: 'User created successfully', loading: false });
        // Refresh users list
        await get().fetchUsers();
        return true;
      } else {
        set({
          error: response.error?.message || 'Failed to create user',
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Create User Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Update an existing user
  updateExistingUser: async (userId: string, userData: any) => {
    try {
      set({ loading: true, error: null, success: null });

      const response = await updateUser(userId, userData);
      console.log("Update User API Response:", response);

      if (response.success) {
        set({ success: 'User updated successfully', loading: false });
        // Refresh users list
        await get().fetchUsers();
        return true;
      } else {
        set({
          error: response.error?.message || 'Failed to update user',
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Update User Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Delete a user
  deleteExistingUser: async (userId: string) => {
    try {
      set({ loading: true, error: null, success: null });

      const response = await deleteUser(userId);
      console.log("Delete User API Response:", response);

      if (response.success) {
        set({ success: 'User deleted successfully', loading: false });
        // Refresh users list
        await get().fetchUsers();
        return true;
      } else {
        set({
          error: response.error?.message || 'Failed to delete user',
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Delete User Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Set error message
  setError: (error: string | null) => set({ error }),

  // Set success message
  setSuccess: (success: string | null) => set({ success }),

  // Reset messages
  resetMessages: () => set({ error: null, success: null }),

  // Reset store to initial state
  resetStore: () => set({
    users: [],
    roles: [],
    dashboardStats: null,
    loading: false,
    error: null,
    success: null
  })
}));

// Register this store for centralized reset
registerStoreReset('admin', () => useAdminStore.getState().resetStore());

export default useAdminStore;
